<script>
import { mapActions, mapGetters } from 'vuex';
import downloader from '@/mixins/downloader';
import RbxConfirmDialog from '@/shared/reybex/RbxConfirmDialog';
import mainViewTabMixin from '@/mixins/mainViewTab';
import snackbarMixin from '@/mixins/snackbar';
import MasterDataForm from './components/MasterDataForm';
import RequiredDataForm from './components/RequiredDataForm';
import RiskAnalysisForm from './components/RiskAnalysisForm';
import DsfaForm from './components/DsfaForm';
import TaskList from '../taskList';
import Documents from './components/Documents';
import Notes from '../notes';
import History from '../history';

export default {
  name: 'ProcedureDirectoryDetails',
  components: {
    MasterDataForm,
    RequiredDataForm,
    RiskAnalysisForm,
    DsfaForm,
    TaskList,
    Documents,
    Notes,
    History,
    RbxConfirmDialog,
  },
  mixins: [mainViewTabMixin, snackbarMixin, downloader],
  data() {
    return {
      query: {},
      params: {},
      procedureDirectory: {},
      isLoading: false,
      isSaving: false,
      hasError: false,
      activeTab: 0,
      isDownloading: false,
      isNewVersionConfirmDialogDisplayed: false,
    };
  },
  computed: {
    ...mapGetters('app', ['auth']),
  },
  created() {
    const { query, params } = this.urlParser('/procedureDirectory/show/:id?', this.path);

    this.query = query;
    this.params = params;
    this.fetchData();
  },
  methods: {
    ...mapActions('procedureDirectoryDetails', [
      'fetchProcedureDirectory',
      'saveProcedureDirectory',
      'copyProcedureDirectory']),
    ...mapActions('procedureDirectoryList', ['downloadReport']),
    fetchData() {
      this.isLoading = true;
      this.hasError = false;

      return this.fetchProcedureDirectory(this.params.id)
        .then(({ data }) => {
          this.procedureDirectory = data;
          this.updateTabTitle({ path: this.path, title: this.procedureDirectory.name });
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isSaving = true;

      return this.saveProcedureDirectory(this.procedureDirectory)
        .then(() => {
          this.showSuccessMessage('Das Verfahren wurde gespeichert.');
        })
        .catch(() => {
          this.showErrorMessage('Die Aktualisierung ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    _downloadReport() {
      this.isDownloading = true;
      const self = this;
      this.downloadReport(this.procedureDirectory.id)
        .then((response) => {
          this.getDownloadContent(response, 'procedure_report.pdf');
        }).catch((error) => {
          console.log(`Fehler: ${error.response}`);
          this.showErrorMessage('Beim Erstellen des Berichts ist ein Fehler aufgetreten.');
        }).finally(() => {
          self.isDownloading = false;
        });
    },
    onNewVersionConfirm() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.isSaving = true;

      this.copyProcedureDirectory(this.procedureDirectory)
        .then((response) => {
          this.showSuccessMessage('Das Verfahren wurde als neue Version angelegt.');
          this.params.id = response.data.id;
          this.fetchData();
        })
        .catch(() => {
          this.showErrorMessage('Das Speichern ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    onNewVersionRefuse() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.save();
    },
  },
};
</script>

<template>
  <div class="procedureDirectory-detail">
    <v-progress-linear v-if="isLoading" :indeterminate="true"/>

    <span
      v-else-if="hasError"
      class="js-error ma-3"
    >An error occurred while fetching procedureDirectory information</span>

    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">

        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            slot="activator"
            :loading="isSaving"
            icon
            @click="isNewVersionConfirmDialogDisplayed = true"
          >
            <v-icon>save</v-icon>
          </v-btn>
          <span>Speichern</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            v-if="auth.hasDownloadRights"
            slot="activator"
            :disabled="isDownloading"
            flat icon
            @click.native="_downloadReport"
          >
            <v-icon small>cloud_download</v-icon>
          </v-btn>
          <span>Bericht herunterladen</span>
        </v-tooltip>
      </v-toolbar>

      <v-tabs v-model="activeTab"
              height="35"
              slider-color="black"
              color="amber">

        <v-tab ripple>Stammdaten</v-tab>
        <v-tab ripple>Weitere Angaben</v-tab>
        <v-tab ripple>Risikoanalyse</v-tab>
        <v-tab ripple>DSFA</v-tab>
        <v-tab ripple>Aufgaben</v-tab>
        <v-tab ripple>Dokumente</v-tab>
        <v-tab ripple>Notizen</v-tab>
        <v-tab ripple>Historie</v-tab>

        <v-tab-item>
          <MasterDataForm :procedure-directory="procedureDirectory" @save="save" />
        </v-tab-item>
        <v-tab-item>
          <RequiredDataForm :procedure-directory="procedureDirectory" @save="save" />
        </v-tab-item>
        <v-tab-item>
          <RiskAnalysisForm :procedure-directory="procedureDirectory" @save="save" />
        </v-tab-item>
        <v-tab-item>
          <DsfaForm :procedure-directory="procedureDirectory" @save="save" />
        </v-tab-item>
        <v-tab-item>
          <task-list
            :parent-resource="'procedureDirectory'"
            :parent-resource-id="procedureDirectory.id"
            @save="save"
            path="/tasks"
          />
        </v-tab-item>
        <v-tab-item>
          <Documents
            :resource="'procedureDirectory'"
            :id="procedureDirectory.id"
            :allowed-file-types="['csv', 'doc', 'pdf', 'rtf', 'tex', 'txt', 'xlsx']"
          />
        </v-tab-item>
        <v-tab-item>
          <notes
            :parent-resource="'procedureDirectory'"
            :parent-resource-id="procedureDirectory.id"
            @save="save"
            path="/notes"
          />
        </v-tab-item>
        <v-tab-item>
          <History
            :domain="'procedureDirectory'"
            :domain-id="procedureDirectory.id"
            path="/history"
          />
        </v-tab-item>
      </v-tabs>

      <rbx-confirm-dialog
        @confirm="onNewVersionConfirm" @refuse="onNewVersionRefuse"
        :message="'Wie soll Ihre Änderung gespeichert werden?'"
        :is-displayed="isNewVersionConfirmDialogDisplayed"
        :is-loading="isLoading"
        :confirm-text="'Speicherung als neue Version'"
        :reject-text="'Überschreibung der alten Version'"
      />
    </template>
  </div>
</template>

<style lang="scss">
.procedureDirectory-detail {
  &-title {
    margin-bottom: 10px;
  }

  &-actions {
    margin: 5px 0;

    .v-btn:first-child {
      border-radius: 5px 0 0 5px;
    }

    .v-btn:last-child {
      border-radius: 0 5px 5px 0;
    }

    .v-btn + .v-btn {
      border-left: 1px solid #fff !important;
    }

    .v-btn {
      margin: 0;
      width: 35px;
      height: 35px;
      border-radius: 0px;
    }
  }

  &-tab {
    padding-top: 10px;

    &-title {
      margin-bottom: 10px;
    }
  }

  &-common-list {
    margin-top: 5px;
    .v-btn {
      margin: 0px;
      width: 30px;
      height: 30px;
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        .v-btn {
          opacity: 1;
        }
      }

      .v-btn {
        opacity: 0;
      }
    }
  }

  .v-tabs {
    &__items {
      padding-bottom: 30px;
    }
  }

  .submit-button {
    margin-top: 20px;
  }
}
</style>
